import * as signalR from '@microsoft/signalr';
import { getAccessToken } from './auth/ownAuthSession';

const SALES_API_URL = process.env.REACT_APP_SALESAPI_URL || 'http://localhost:5238';

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private companyId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000; // 3 saniye
  private statusChangeCallbacks: ((status: signalR.HubConnectionState) => void)[] = [];

  /**
   * SignalR bağlantısını başlat
   */
  async startConnection(companyId: string): Promise<void> {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      console.log('[SignalR] 🟢 Zaten bağlı, yeni bağlantı yapılmayacak');
      return;
    }

    this.companyId = companyId;

    // Mevcut bağlantıyı kapat
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('[SignalR] ❌ Eski bağlantı kapatılırken hata:', error);
      }
    }

    // Yeni bağlantı oluştur
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${SALES_API_URL}/hubs/orders`, {
        withCredentials: false,
        skipNegotiation: false,
        // Hub, şirket grubuna katılımda oturum token'ını doğrular; her (yeniden) bağlanmada taze token.
        accessTokenFactory: async () => (await getAccessToken()) ?? '',
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // İlk 3 deneme: 3 saniye
          // Sonraki denemeler: 10 saniye
          if (retryContext.previousRetryCount < 3) {
            return 3000;
          }
          return 10000;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Bağlantı durumu değişikliklerini dinle
    this.connection.onclose((error) => {
      console.log('[SignalR] 🔴 Bağlantı kapandı', error);
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      this.attemptReconnect();
    });

    this.connection.onreconnecting((error) => {
      console.log('[SignalR] 🟡 Yeniden bağlanılıyor...', error);
      this.notifyStatusChange(signalR.HubConnectionState.Reconnecting);
    });

    this.connection.onreconnected((connectionId) => {
      console.log('[SignalR] 🟢 Yeniden bağlandı! Connection ID:', connectionId);
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      this.reconnectAttempts = 0;
      
      // Yeniden gruba katıl
      if (this.companyId) {
        this.joinCompanyGroup(this.companyId);
      }
    });

    try {
      console.log('[SignalR] 🔄 Bağlantı başlatılıyor...', `${SALES_API_URL}/hubs/orders`);
      await this.connection.start();
      console.log('[SignalR] 🟢 Bağlantı başarılı! Connection ID:', this.connection.connectionId);
      this.notifyStatusChange(signalR.HubConnectionState.Connected);
      this.reconnectAttempts = 0;

      // Şirket grubuna katıl
      await this.joinCompanyGroup(companyId);

    } catch (error) {
      console.error('[SignalR] ❌ Bağlantı başlatılamadı:', error);
      this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      this.attemptReconnect();
    }
  }

  /**
   * Şirket grubuna katıl
   */
  private async joinCompanyGroup(companyId: string): Promise<void> {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn('[SignalR] ⚠️ Bağlantı yok, gruba katılınamıyor');
      return;
    }

    try {
      await this.connection.invoke('JoinCompanyGroup', companyId);
      console.log(`[SignalR] 🎯 Şirket grubuna katılındı: Company_${companyId}`);
    } catch (error) {
      console.error('[SignalR] ❌ Gruba katılırken hata:', error);
    }
  }

  /**
   * Manuel yeniden bağlanma denemesi
   */
  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SignalR] ❌ Maksimum yeniden bağlanma denemesi aşıldı');
      return;
    }

    this.reconnectAttempts++;
    console.log(`[SignalR] 🔄 Yeniden bağlanma denemesi ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

    setTimeout(async () => {
      if (this.companyId) {
        try {
          await this.startConnection(this.companyId);
        } catch (error) {
          console.error('[SignalR] ❌ Yeniden bağlanma başarısız:', error);
        }
      }
    }, this.reconnectDelay);
  }

  /**
   * Yeni sipariş bildirimi dinle
   */
  onNewOrder(callback: (order: any) => void): void {
    if (!this.connection) {
      console.warn('[SignalR] ⚠️ Bağlantı yok, event listener eklenemedi');
      return;
    }

    this.connection.on('ReceiveNewOrder', (order: any) => {
      console.log('[SignalR] 📥 Yeni sipariş bildirimi alındı:', order);
      callback(order);
    });
  }

  /**
   * Sipariş durumu güncelleme dinle
   */
  onOrderStatusUpdated(callback: (update: any) => void): void {
    if (!this.connection) {
      console.warn('[SignalR] ⚠️ Bağlantı yok, event listener eklenemedi');
      return;
    }

    this.connection.on('ReceiveOrderStatusUpdate', (update: any) => {
      console.log('[SignalR] 🔄 Sipariş durumu güncellendi:', update);
      callback(update);
    });
  }

  /**
   * Bağlantı durumu değişikliği callback'i ekle
   */
  onStatusChange(callback: (status: signalR.HubConnectionState) => void): void {
    this.statusChangeCallbacks.push(callback);
    
    // Mevcut durumu hemen bildir
    if (this.connection) {
      callback(this.connection.state);
    }
  }

  /**
   * Bağlantı durumu değişikliklerini bildir
   */
  private notifyStatusChange(status: signalR.HubConnectionState): void {
    this.statusChangeCallbacks.forEach(callback => callback(status));
  }

  /**
   * Mevcut bağlantı durumunu al
   */
  getConnectionState(): signalR.HubConnectionState {
    return this.connection?.state || signalR.HubConnectionState.Disconnected;
  }

  /**
   * Bağlantıyı kapat
   */
  async stopConnection(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('[SignalR] 🔴 Bağlantı kapatıldı');
        this.notifyStatusChange(signalR.HubConnectionState.Disconnected);
      } catch (error) {
        console.error('[SignalR] ❌ Bağlantı kapatılırken hata:', error);
      }
      this.connection = null;
    }
  }
}

// Singleton instance
export const signalRService = new SignalRService();
export default signalRService;

